import { ReactNode } from "react";
import classNames from "classnames";
import style from "./tab-bar.module.css";

export interface TabBarOption {
  id: string;
  renderer: string | (() => ReactNode);
  disabled?: boolean;
}

interface TabBarProps<T extends TabBarOption> {
  options: T[];
  selected: string;
  onChange: (id: string) => void;
  customStyle?: React.CSSProperties;
  theme?: "dark" | "light";
}

export default function TabBar<T extends TabBarOption>({
  options,
  selected,
  onChange,
  customStyle = {},
  theme = "light",
}: TabBarProps<T>) {
  const selectedIndex = options.findIndex((option) => option.id === selected);

  function renderTab(option: T) {
    const isDisabled = !!option.disabled;
    const isSelected = option.id === selected;
    return (
      <div
        key={option.id}
        data-testid={`tab-${option.id}`}
        onClick={() => !isDisabled && onChange(option.id)}
        className={classNames(style.tab, style[theme], {
          [style.selectedTab]: isSelected,
          [style.disabled]: isDisabled,
        })}
      >
        {typeof option.renderer === "string" ? <span>{option.renderer}</span> : option.renderer()}
      </div>
    );
  }

  function renderTabs() {
    return options.map((option) => {
      return renderTab(option);
    });
  }

  return (
    <div className={classNames(style.container, style[theme])} style={customStyle}>
      <div className={style.tabs}>
        <span
          style={{
            width: `${100 / options.length}%`,
            transform: `translateX(${selectedIndex * 100}%)`,
          }}
          className={classNames(style.glider, style[theme])}
        />
        {renderTabs()}
      </div>
    </div>
  );
}
